"use client";

import React from "react";
import { useEffect, useState, useRef, useCallback } from "react";
import { useFetchCompanyInfo } from "src/app/lib/fetchCompanyInfo";

const Counter: React.FC<{ variant?: boolean }> = ({ variant }) => {
  const { data } = useFetchCompanyInfo();
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const counterRef = useRef<HTMLHeadingElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const startAnimation = useCallback(() => {
    if (hasAnimated || !data?.companyStat?.completed_transaction) {
      return;
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const end = data.companyStat.completed_transaction;
    const duration = 2000; // 2 seconds
    const steps = Math.min(end, 60); // Limit to 60 steps for performance
    const increment = end / steps;
    const stepTime = duration / steps;

    let currentCount = 0;
    let step = 0;

    timerRef.current = setInterval(() => {
      step += 1;
      currentCount = Math.round(increment * step);

      // Ensure we don't exceed the target
      if (currentCount >= end || step >= steps) {
        currentCount = end;
        setCount(currentCount);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setHasAnimated(true);
      } else {
        setCount(currentCount);
      }
    }, stepTime);
  }, [data, hasAnimated]);

  useEffect(() => {
    if (!data?.companyStat?.completed_transaction) {
      return;
    }

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          startAnimation();
        }
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observerRef.current.observe(counterRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [data, startAnimation, hasAnimated]);

  // Reset animation state when data changes
  useEffect(() => {
    setCount(0);
    setHasAnimated(false);
  }, [data?.companyStat?.completed_transaction]);

  if (variant) {
    return <span ref={counterRef}>{count}</span>;
  }

  return (
    <div className="w-[50%] mx-auto border min-w-[18rem] border-white text-white aspect-square grid place-items-center rounded-full">
      <div className="items flex flex-col items-center">
        <h4 ref={counterRef} className="font-bold text-[5.5rem]">
          {count}
        </h4>
        <p className="mt-4 text-center text-white">Completed Transaction</p>
      </div>
    </div>
  );
};

export default Counter;
